import React, { useState, useRef, useEffect, useCallback } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  MessageSquare,
  X,
  Send,
  Loader2,
  Bot,
  User,
  Minimize2,
  Maximize2,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Phone,
  PhoneOff
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// WebRTC Realtime Audio Client for CRM Assistant
class AssistantAudioClient {
  constructor(onMessage, onStatusChange, onTranscript) {
    this.peerConnection = null;
    this.dataChannel = null;
    this.audioElement = null;
    this.localStream = null;
    this.onMessage = onMessage;
    this.onStatusChange = onStatusChange;
    this.onTranscript = onTranscript;
    this.fullTranscript = '';
  }

  async init(voiceConfig) {
    try {
      this.onStatusChange('connecting');
      
      // Get session from backend
      const tokenResponse = await fetch(`${BACKEND_URL}/api/realtime/session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!tokenResponse.ok) {
        throw new Error("Failed to get session token");
      }
      
      const data = await tokenResponse.json();
      if (!data.client_secret?.value) {
        throw new Error("Invalid session response");
      }

      // Create WebRTC peer connection
      this.peerConnection = new RTCPeerConnection();
      this.setupAudioElement();
      await this.setupLocalAudio();
      this.setupDataChannel(voiceConfig);

      // Create and send offer
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      // Send offer to backend and get answer
      const response = await fetch(`${BACKEND_URL}/api/realtime/negotiate`, {
        method: "POST",
        body: offer.sdp,
        headers: {
          "Content-Type": "application/sdp",
          "Authorization": `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error("Negotiation failed");
      }

      const { sdp: answerSdp } = await response.json();
      const answer = {
        type: "answer",
        sdp: answerSdp
      };

      await this.peerConnection.setRemoteDescription(answer);
      this.onStatusChange('connected');
      
    } catch (error) {
      console.error("Failed to initialize assistant voice:", error);
      this.onStatusChange('error');
      throw error;
    }
  }

  setupAudioElement() {
    this.audioElement = document.createElement("audio");
    this.audioElement.autoplay = true;
    document.body.appendChild(this.audioElement);

    this.peerConnection.ontrack = (event) => {
      this.audioElement.srcObject = event.streams[0];
    };
  }

  async setupLocalAudio() {
    this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    this.localStream.getTracks().forEach(track => {
      this.peerConnection.addTrack(track, this.localStream);
    });
  }

  setupDataChannel(voiceConfig) {
    this.dataChannel = this.peerConnection.createDataChannel("oai-events");
    
    this.dataChannel.onopen = () => {
      if (voiceConfig) {
        this.sendSessionConfig(voiceConfig);
      }
    };
    
    this.dataChannel.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleRealtimeEvent(data);
      } catch (e) {
        console.error("Failed to parse event:", e);
      }
    };
  }

  sendSessionConfig(voiceConfig) {
    const sessionUpdate = {
      type: "session.update",
      session: {
        modalities: voiceConfig.modalities || ["text", "audio"],
        voice: voiceConfig.voice || "alloy",
        instructions: voiceConfig.instructions,
        input_audio_format: "pcm16",
        output_audio_format: "pcm16",
        input_audio_transcription: {
          model: "whisper-1"
        },
        turn_detection: voiceConfig.turn_detection || {
          type: "server_vad",
          threshold: 0.5,
          prefix_padding_ms: 300,
          silence_duration_ms: 500
        },
        temperature: 0.7
      }
    };
    
    this.sendMessage(sessionUpdate);
  }

  handleRealtimeEvent(event) {
    switch (event.type) {
      case 'conversation.item.input_audio_transcription.completed':
        if (event.transcript) {
          this.onTranscript(event.transcript, 'user');
        }
        break;
        
      case 'response.audio_transcript.done':
        if (event.transcript) {
          this.onTranscript(event.transcript, 'assistant');
        }
        break;
        
      case 'response.done':
        this.onMessage({ type: 'response_complete' });
        break;
        
      case 'error':
        console.error("Realtime API error:", event.error);
        this.onMessage({ type: 'error', error: event.error });
        break;
    }
  }

  sendMessage(message) {
    if (this.dataChannel && this.dataChannel.readyState === 'open') {
      this.dataChannel.send(JSON.stringify(message));
    }
  }

  disconnect() {
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
    }
    if (this.peerConnection) {
      this.peerConnection.close();
    }
    if (this.audioElement) {
      this.audioElement.remove();
    }
    this.onStatusChange('disconnected');
  }

  toggleMute(muted) {
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach(track => {
        track.enabled = !muted;
      });
    }
  }
}

const CRMAssistant = ({ context = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm your CRM assistant. Ask me about your clients, deals, KPIs, or anything else. Try:\n• \"How many clients do I have?\"\n• \"Who hasn't been contacted this week?\"\n• \"What deals are in underwriting?\"\n\nYou can also tap the mic to talk to me!"
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Voice state
  const [voiceAvailable, setVoiceAvailable] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState('disconnected'); // disconnected, connecting, connected, error
  const [isMuted, setIsMuted] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const audioClientRef = useRef(null);
  const [voiceConfig, setVoiceConfig] = useState(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
      // Check voice availability
      checkVoiceAvailability();
    }
  }, [isOpen, isMinimized]);
  
  useEffect(() => {
    return () => {
      if (audioClientRef.current) {
        audioClientRef.current.disconnect();
      }
    };
  }, []);

  const checkVoiceAvailability = async () => {
    try {
      const response = await api.get('/assistant/voice-config');
      setVoiceConfig(response.data);
      setVoiceAvailable(response.data.available);
    } catch (error) {
      console.error('Voice config error:', error);
      setVoiceAvailable(false);
    }
  };

  const handleVoiceMessage = useCallback((message) => {
    if (message.type === 'error') {
      toast.error('Voice error: ' + (message.error?.message || 'Unknown error'));
    }
  }, []);

  const handleVoiceStatusChange = useCallback((status) => {
    setVoiceStatus(status);
    if (status === 'connected') {
      setIsListening(true);
      toast.success('Voice connected! Speak your question.');
    } else if (status === 'error') {
      setIsListening(false);
      toast.error('Voice connection failed');
    } else if (status === 'disconnected') {
      setIsListening(false);
    }
  }, []);

  const handleVoiceTranscript = useCallback((text, speaker) => {
    setMessages(prev => [...prev, {
      role: speaker === 'user' ? 'user' : 'assistant',
      content: text,
      isVoice: true
    }]);
  }, []);

  const startVoiceSession = async () => {
    if (!voiceAvailable) {
      toast.error('Voice feature is not available');
      return;
    }

    try {
      setVoiceStatus('connecting');
      
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());

      // Get fresh voice config
      const configRes = await api.get('/assistant/voice-config');
      
      audioClientRef.current = new AssistantAudioClient(
        handleVoiceMessage,
        handleVoiceStatusChange,
        handleVoiceTranscript
      );
      
      await audioClientRef.current.init(configRes.data);
      
    } catch (error) {
      setVoiceStatus('disconnected');
      if (error.name === 'NotAllowedError') {
        toast.error('Microphone access denied');
      } else {
        toast.error('Failed to start voice: ' + error.message);
      }
    }
  };

  const stopVoiceSession = () => {
    if (audioClientRef.current) {
      audioClientRef.current.disconnect();
      audioClientRef.current = null;
    }
    setIsListening(false);
    setVoiceStatus('disconnected');
  };

  const toggleMute = () => {
    if (audioClientRef.current) {
      audioClientRef.current.toggleMute(!isMuted);
      setIsMuted(!isMuted);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await api.post('/assistant/chat', {
        message: userMessage,
        context: context,
        conversation_id: conversationId
      });

      setConversationId(response.data.conversation_id);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        data: response.data.data,
        actions: response.data.actions
      }]);
    } catch (error) {
      toast.error('Failed to get response');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Sorry, I couldn't process that request. Please try again."
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickQuestions = [
    "How many clients do I have?",
    "Who hasn't been contacted?",
    "What are my monthly metrics?",
    "Deals in underwriting?"
  ];

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-6 h-14 w-14 rounded-full shadow-lg z-50"
        data-testid="open-assistant-btn"
      >
        <MessageSquare className="w-6 h-6" />
      </Button>
    );
  }

  return (
    <Card className={`fixed bottom-20 right-6 shadow-2xl z-50 flex flex-col transition-all duration-200 ${
      isMinimized ? 'w-72 h-14' : 'w-96 h-[550px]'
    }`} data-testid="assistant-panel">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-slate-900 text-white rounded-t-lg">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5" />
          <span className="font-medium">CRM Assistant</span>
          {isListening && (
            <Badge variant="secondary" className="bg-green-500/20 text-green-300 text-xs">
              <span className="mr-1 h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse inline-block" />
              Voice Active
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-white hover:bg-slate-700"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-white hover:bg-slate-700"
            onClick={() => {
              if (isListening) stopVoiceSession();
              setIsOpen(false);
            }}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <ScrollArea className="flex-1 p-3">
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-slate-600" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                      msg.role === 'user'
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-100 text-slate-900'
                    }`}
                  >
                    {msg.isVoice && (
                      <div className="flex items-center gap-1 text-xs opacity-60 mb-1">
                        <Volume2 className="w-3 h-3" />
                        <span>Voice</span>
                      </div>
                    )}
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                    
                    {/* Show data if available */}
                    {msg.data && (
                      <div className="mt-2 p-2 bg-white rounded border text-xs">
                        {msg.data.total_contacts && (
                          <p><strong>Total Contacts:</strong> {msg.data.total_contacts}</p>
                        )}
                        {msg.data.uncontacted_count !== undefined && (
                          <p><strong>Uncontacted:</strong> {msg.data.uncontacted_count}</p>
                        )}
                        {msg.data.month && (
                          <div>
                            <p><strong>{msg.data.month}</strong></p>
                            {msg.data.deals_closed !== undefined && <p>Deals Closed: {msg.data.deals_closed}</p>}
                            {msg.data.new_contacts !== undefined && <p>New Contacts: {msg.data.new_contacts}</p>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-7 h-7 rounded-full bg-slate-900 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              ))}
              
              {loading && (
                <div className="flex gap-2 justify-start">
                  <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-slate-600" />
                  </div>
                  <div className="bg-slate-100 rounded-lg px-3 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Quick Questions */}
          {messages.length <= 2 && !isListening && (
            <div className="px-3 pb-2">
              <div className="flex flex-wrap gap-1">
                {quickQuestions.map((q, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => {
                      setInput(q);
                      setTimeout(sendMessage, 100);
                    }}
                  >
                    {q}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Voice Controls */}
          {isListening && (
            <div className="px-3 pb-2">
              <div className="flex items-center justify-center gap-3 p-2 bg-green-500/10 rounded-lg border border-green-500/20">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleMute}
                  className={isMuted ? 'text-red-500' : 'text-green-600'}
                >
                  {isMuted ? <MicOff className="w-4 h-4 mr-1" /> : <Mic className="w-4 h-4 mr-1" />}
                  {isMuted ? 'Unmute' : 'Mute'}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={stopVoiceSession}
                >
                  <PhoneOff className="w-4 h-4 mr-1" />
                  End Voice
                </Button>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t">
            <div className="flex gap-2">
              {/* Voice Button */}
              <Button
                variant={isListening ? "destructive" : "outline"}
                size="icon"
                onClick={isListening ? stopVoiceSession : startVoiceSession}
                disabled={voiceStatus === 'connecting'}
                className="flex-shrink-0"
                data-testid="assistant-voice-btn"
                title={voiceAvailable ? "Start voice conversation" : "Voice not available"}
              >
                {voiceStatus === 'connecting' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isListening ? (
                  <PhoneOff className="w-4 h-4" />
                ) : (
                  <Phone className="w-4 h-4" />
                )}
              </Button>
              
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={isListening ? "Voice active - speak your question..." : "Ask me anything..."}
                className="flex-1"
                disabled={isListening}
                data-testid="assistant-input"
              />
              <Button
                onClick={sendMessage}
                disabled={loading || !input.trim() || isListening}
                size="icon"
                data-testid="assistant-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

export default CRMAssistant;
